import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { LangProvider } from "@/i18n/LangContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Solutions from "@/pages/Solutions";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";

const serviceIds = ["b2b-advertising", "websites", "crm", "ai-secretary", "business-email"];

const baseRoutes = [
  { path: "/", element: <Home /> },
  ...serviceIds.map((id) => ({ path: `/${id}`, element: <ServicePage key={id} id={id} /> })),
  { path: "/projects", element: <Projects /> },
  { path: "/projects/:slug", element: <ProjectDetail /> },
  { path: "/solutions", element: <Solutions /> },
  { path: "/about", element: <About /> },
  { path: "/contact", element: <Contact /> },
  { path: "/privacy-policy", element: <Legal kind="privacy" /> },
  { path: "/cookies", element: <Legal kind="cookies" /> },
];

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Shell() {
  return (
    <LangProvider>
      <div className="min-h-screen bg-ink text-white antialiased">
        <Header />
        <main>
          <Routes>
            {["", "/en"].map((p) =>
              baseRoutes.map((r) => {
                const full = p + r.path;
                return <Route key={full || "/"} path={full === "" ? "/" : full} element={r.element} />;
              })
            )}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        <CookieConsent />
        <Toaster position="bottom-center" richColors />
      </div>
    </LangProvider>
  );
}

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollManager />
        <Shell />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
